import { useState } from 'react'
import { App as AntdApp, Table, Input, Space, Tag, Button, Popconfirm, Segmented } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { canManageKnowledge, KNOWLEDGE_STATUS_COLOR, KNOWLEDGE_STATUS_LABEL } from '@/modules/knowledge/constants'
import { useKnowledgeList, usePublishKnowledge, useArchiveKnowledge } from '@/modules/knowledge/queries'
import { useAuthStore } from '@/stores/authStore'
import type { KnowledgeStatus } from '@/types/knowledge'
import ErrorResult from '@/components/feedback/ErrorResult'
import KnowledgeDetailDrawer from './components/KnowledgeDetailDrawer'

function truncateContent(content: string, maxLen = 120): string {
  if (!content) return ''
  return content.length > maxLen ? content.slice(0, maxLen) + '...' : content
}

export default function KnowledgeListPage() {
  const { message } = AntdApp.useApp()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<KnowledgeStatus>('published')
  const [detailId, setDetailId] = useState<number | null>(null)
  const roles = useAuthStore((s) => s.roles)
  const canManage = canManageKnowledge(roles)

  const { data, isLoading, isError, error, refetch } = useKnowledgeList({
    page,
    page_size: pageSize,
    keyword: keyword || undefined,
    status: canManage ? status : undefined,
  })

  const publishMutation = usePublishKnowledge()
  const archiveMutation = useArchiveKnowledge()

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <Input
          placeholder="搜索知识标题/内容"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 300 }}
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); setPage(1) }}
        />
        {canManage && (
          <Segmented
            value={status}
            options={[
              { label: KNOWLEDGE_STATUS_LABEL.published, value: 'published' },
              { label: KNOWLEDGE_STATUS_LABEL.draft, value: 'draft' },
            ]}
            onChange={(value) => {
              setStatus(value as KnowledgeStatus)
              setPage(1)
            }}
          />
        )}
      </Space>

      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) },
        }}
        columns={[
          { title: '标题', dataIndex: 'title', ellipsis: true },
          {
            title: '关键词', dataIndex: 'keywords', width: 150,
            render: (v: string[]) => v?.length ? (
              <Space size={[0, 4]} wrap>
                {v.slice(0, 3).map((kw, i) => <Tag key={i} color="blue">{kw}</Tag>)}
              </Space>
            ) : '-',
          },
          {
            title: '来源工单', dataIndex: 'source_ticket_id', width: 100,
            render: (v: number | null) => v ? `#${v}` : '-',
          },
          {
            title: '状态', dataIndex: 'status', width: 80,
            render: (v: KnowledgeStatus) => (
              <Tag color={KNOWLEDGE_STATUS_COLOR[v]}>
                {KNOWLEDGE_STATUS_LABEL[v]}
              </Tag>
            ),
          },
          {
            title: '内容摘要', dataIndex: 'content', ellipsis: true,
            render: (v: string) => truncateContent(v),
          },
          {
            title: '更新时间', dataIndex: 'updated_at', width: 100,
            render: (v: string) => v?.slice(0, 10),
          },
          {
            title: '操作', width: 160, key: 'actions',
            render: (_: unknown, r: { id: number; status: KnowledgeStatus }) => (
              <Space size="small">
                <Button type="link" size="small" onClick={() => setDetailId(r.id)}>
                  详情
                </Button>
                {canManage && r.status === 'draft' && (
                  <Button
                    type="link"
                    size="small"
                    loading={publishMutation.isPending}
                    onClick={() =>
                      publishMutation.mutate(
                        { entryId: r.id },
                        {
                          onSuccess: () => message.success('发布成功'),
                          onError: (err) =>
                            message.error(err instanceof Error ? err.message : '发布失败，当前角色可能无此权限'),
                        },
                      )
                    }
                  >
                    发布
                  </Button>
                )}
                {canManage && r.status === 'published' && (
                  <Popconfirm
                    title="确定归档该知识条目？"
                    description="归档后将从搜索列表中隐藏"
                    onConfirm={() =>
                      archiveMutation.mutate(
                        { entryId: r.id },
                        {
                          onSuccess: () => message.success('归档成功'),
                          onError: (err) =>
                            message.error(err instanceof Error ? err.message : '归档失败，当前角色可能无此权限'),
                        },
                      )
                    }
                    okText="确定归档"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger loading={archiveMutation.isPending}>
                      归档
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      {/* 详情抽屉 */}
      <KnowledgeDetailDrawer
        entryId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  )
}
